import React, { useState, useEffect, useRef } from "react";
import "./Tabs.css";
import { useAuth } from "../auth/useAuth";
import { useTrack } from "../analytics/TrackingContext";
import { useFeatureFlags } from "../features/FeatureFlagsContext";

import Autonomia from "./Autonomia";
import CargaBateria from "./CargaBateria";
import ConsumoVehiculo from "./ConsumoVehiculo";
import PotenciaCarga from "./PotenciaCarga";
import TiempoCargando from "./TiempoCargando";

import GastoMensualCarga from "./GastoMensualCarga";
import GastoMensualCombustible from "./GastoMensualCombustible";
import ComparativaCostes from "./ComparativaCostes";

import TCOFlotas from "./TCOFlotas";

function Tabs() {
  const { user } = useAuth();
  const { trackEvent } = useTrack();
  const { isEnabled, flags } = useFeatureFlags();
  const [activeTab, setActiveTab] = useState(null);
  const [resultadoCarga, setResultadoCarga] = useState(null);
  const [resultadoCombustible, setResultadoCombustible] = useState(null);
  const [nombreVehiculoElectrico, setNombreVehiculoElectrico] = useState('');
  const [nombreVehiculoCombustion, setNombreVehiculoCombustion] = useState('');
  const [showSegundaComparativa, setShowSegundaComparativa] = useState(false);
  const [capacidadBateria, setCapacidadBateria] = useState('');

  const nombreAsesor = user ? `${user.nombre} ${user.apellidos || ''}`.trim() : '';
  const concesionario = user?.concesionario_name || '';

  // Definición estática de pestañas; el orden lo dicta sort_order de los flags
  const TAB_DEFS = [
    { id: 'tco', flagKey: 'tab_renting_tcu',    label: 'Renting TCU' },
    { id: 1,     flagKey: 'tab_compara_gastos',  label: 'Compara Gastos Entre VE y Combustible' },
    { id: 2,     flagKey: 'tab_datos_ve',        label: 'Calcula Rápidamente Datos de VE' },
  ];

  const sortedTabs = [...TAB_DEFS].sort((a, b) => {
    const oa = flags[a.flagKey]?.sort_order ?? 99;
    const ob = flags[b.flagKey]?.sort_order ?? 99;
    return oa - ob;
  });

  // Seleccionar la primera pestaña habilitada al cargar o cuando cambia el orden
  useEffect(() => {
    const enabledTabs = sortedTabs.filter(t => isEnabled(t.flagKey));
    if (!activeTab || !enabledTabs.find(t => t.id === activeTab)) {
      if (enabledTabs.length > 0) setActiveTab(enabledTabs[0].id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flags]);

  useEffect(() => {
    if (!activeTab) return;
    const sections = { 1: 'compara_gastos', 2: 'datos_ve', tco: 'renting_tco' };
    trackEvent('section_view', {
      screen: 'calculator',
      section: sections[activeTab] || String(activeTab),
    });
  }, [activeTab, trackEvent]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const tabsRef = useRef(null);

  // Keyboard navigation for tabs (Arrow Left/Right)
  const handleTabKeyDown = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    const buttons = tabsRef.current?.querySelectorAll('[role="tab"]');
    if (!buttons?.length) return;
    const idx = Array.from(buttons).findIndex(b => b === document.activeElement);
    if (idx === -1) return;
    let next = e.key === 'ArrowRight' ? idx + 1 : idx - 1;
    if (next < 0) next = buttons.length - 1;
    if (next >= buttons.length) next = 0;
    buttons[next].focus();
    buttons[next].click();
  };

  return (
    <div className="tabs-container">
      {/* Cabecera del asesor autenticado */}
      {user && (
        <div className="asesor-header">
          <span className="asesor-header-text">
            <strong>{nombreAsesor}</strong>
            {concesionario && (
              <>
                <span className="asesor-separador">·</span>
                {concesionario}
              </>
            )}
          </span>
        </div>
      )}

      {/* Botones de navegación (Pestañas) */}
      <div className="tabs" role="tablist" aria-label="Secciones de la calculadora" ref={tabsRef} onKeyDown={handleTabKeyDown}>
        {sortedTabs.map(tab => isEnabled(tab.flagKey) && (
          <button
            key={tab.id}
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={activeTab === tab.id}
            aria-controls={`panel-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
            className={activeTab === tab.id ? "active" : ""}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenedor de pestañas con animación */}
      <div className="tabs-content">
        <div role="tabpanel" id="panel-2" aria-labelledby="tab-2" className={`tab-panel ${activeTab === 2 ? "active" : "hidden"}`}>
          <div className="capacidad-global-container">
            <label htmlFor="global_capacidad_bateria" className="capacidad-global-label">
              Capacidad de la batería (kWh)
            </label>
            <input
              id="global_capacidad_bateria"
              type="number"
              value={capacidadBateria}
              onChange={(e) => setCapacidadBateria(e.target.value)}
              placeholder="Ej: 64"
              min={0}
              className="capacidad-global-input"
            />
          </div>
          <div className="app-container">
            {isEnabled('comp_carga_bateria') && <CargaBateria capacidadBateria={capacidadBateria} />}
            {isEnabled('comp_tiempo_cargando') && <TiempoCargando capacidadBateria={capacidadBateria} />}
            {isEnabled('comp_potencia_carga') && <PotenciaCarga capacidadBateria={capacidadBateria} />}
          </div>
          <div className="app-container">
            {isEnabled('comp_autonomia') && <Autonomia capacidadBateria={capacidadBateria} />}
            {isEnabled('comp_consumo_vehiculo') && <ConsumoVehiculo capacidadBateria={capacidadBateria} />}
          </div>
        </div>

        <div role="tabpanel" id="panel-1" aria-labelledby="tab-1" className={`tab-panel ${activeTab === 1 ? "active" : "hidden"}`}>
          <div className="app-container-gasto" style={{ position: 'relative' }}>
            {isEnabled('comp_gasto_carga') && <GastoMensualCarga
              setResultado={setResultadoCarga}
              nombreVehiculo={nombreVehiculoElectrico}
              setNombreVehiculo={setNombreVehiculoElectrico}
            />}
            {resultadoCarga !== null && resultadoCombustible !== null && (
              <div className="cajita-diferencia">
                <p style={{ margin: '0 0 6px', fontSize: '0.78rem', opacity: 0.7 }}>Diferencia de coste mensual</p>
                <p style={{ margin: 0, fontSize: '1.3rem', fontWeight: 'bold' }}>
                  {Math.abs(resultadoCombustible - resultadoCarga).toFixed(2)} €/mes
                </p>
              </div>
            )}
            {isEnabled('comp_gasto_combustible') && <GastoMensualCombustible
              setResultado={setResultadoCombustible}
              nombreVehiculo={nombreVehiculoCombustion}
              setNombreVehiculo={setNombreVehiculoCombustion}
            />}
          </div>
          <div className='app-container-comparativa'>
            {isEnabled('comp_comparativa') && <ComparativaCostes
             gastoMensualCarga={resultadoCarga}
             gastoMensualCombustible={resultadoCombustible}
             nombreVehiculoElectrico={nombreVehiculoElectrico}
             nombreVehiculoCombustion={nombreVehiculoCombustion}
            />}
            {showSegundaComparativa && isEnabled('comp_comparativa') && (
              <ComparativaCostes
               key="comparativa-2"
               gastoMensualCarga={resultadoCarga}
               gastoMensualCombustible={resultadoCombustible}
               nombreVehiculoElectrico={nombreVehiculoElectrico}
               nombreVehiculoCombustion={nombreVehiculoCombustion}
               onClose={() => setShowSegundaComparativa(false)}
              />
            )}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', margin: '0 0 16px' }}>
            {!showSegundaComparativa && (
              <div className="tooltip-wrapper">
                <button
                  className="calc"
                  onClick={() => setShowSegundaComparativa(true)}
                  style={{ fontSize: '1.4rem', padding: '6px 18px', lineHeight: 1 }}
                >
                  +
                </button>
                <span className="tooltip-text tooltip-up">Añadir nueva comparativa</span>
              </div>
            )}
          </div>
        </div>

        <div role="tabpanel" id="panel-tco" aria-labelledby="tab-tco" className={`tab-panel ${activeTab === "tco" ? "active" : "hidden"}`}>
          {isEnabled('comp_tco_flotas') && <TCOFlotas />}
        </div>
      </div>
    </div>
  );
}

export default Tabs;
